import { styled } from 'styled-components';
import React from 'react';
import {verticalGap} from '@sunwebgroup/ui-components';

import { TravelInsuranceService } from './TravelInsuranceService';
import { TransferService } from './TransferService';
import { CancellationInsuranceService } from './CancellationInsuranceService';
import { CarRentalService } from './CarRentalService/CarRentalService';
import { VisaService } from './VisaService';
import { RebookingService } from './RebookingService';
import { BabyCotService } from './BabyCotService';
import { DutchweekValThorensService } from './DutchweekValThorensService';
import { DutchweekService } from './DutchweekService';
import { MealplanDPService } from './MealplanDPService';
import { LuggageService } from './LuggageService';
import { BookingRedeemerService } from './BookingRedeemerService';
import { CheckoutMessage } from '@/scopes/checkout/components/CheckoutMessage';
import { ParticipantsBadgeForm } from '@/scopes/checkout/components/ParticipantsBadgeForm';
import { ComedyService } from "@/scopes/checkout/components/services/ComedyService";
import { MealplanService } from '@/scopes/checkout/components/services/MealplanService';
import { MealplanExternalService } from '@/scopes/checkout/components/services/MealplanExternalService';
import { BreakfastService } from '@/scopes/checkout/components/services/BreakfastService';
import { DinnerService } from '@/scopes/checkout/components/services/DinnerService';
import { BreakfastArrivalBusService } from '@/scopes/checkout/components/services/BreakfastArrivalBusService';
import { SkiPassService } from '@/scopes/checkout/components/services/SkiAncillariesService';
import { SkiMaterialService } from '@/scopes/checkout/components/services/SkiAncillariesService/SkiMaterialService';
import { SkiLessonService } from '@/scopes/checkout/components/services/SkiAncillariesService/SkiLessonService';
import { SkiTransferService } from '@/scopes/checkout/components/services/SkiTransferService';
import {ParkingAirportService} from "@/scopes/checkout/components/services/ParkingAirportService";
import {SkiCarriageService} from "@/scopes/checkout/components/services/SkiCarriageService";
import {PartyTicketsService} from "@/scopes/checkout/components/services/PartyTicketsService";
import {
  CarRentalFilteringService
} from '@/scopes/checkout/components/services/CarRentalFilteringService/CarRentalFilteringService';
import { SweaterService, TShirtService } from '@/scopes/checkout/components/services/ClothingService';

import { useCheckoutSid } from '@/scopes/checkout/domain/hooks';
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags';
import { useCMSPageField } from '@/cms/fields';
import { ServiceTypeApi } from '@/scopes/checkout/infrastructure/api/types';
import { GolfService } from '@/scopes/checkout/components/services/GolfService';
import { BridgeService } from '@/scopes/checkout/components/services/BridgeService';
import { DinnerVouchersService } from '@/scopes/checkout/components/services/DinnerVouchersService';
import { EsimService } from '@/scopes/checkout/components/services/EsimService';
import { BikeRentalService } from '@/scopes/checkout/components/services/BikeRentalService';

const Wrapper = styled.div`
  ${verticalGap('spacing_lg')}
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export function Services() {
  const sid = useCheckoutSid();
  const showCarRentalFilteringService = useFeatureFlag('SHOW_CAR_RENTAL_FILTERING_SERVICE');

  const items = useCMSPageField('Checkout.items')?.items ?? [];

  if (!sid || !items) {
    return null;
  }

  return (
    <Wrapper>
      {items.map((item, index) => {
          const { type, componentId, aName } = item;
          switch (type) {
            case ServiceTypeApi.ConditionalMessages:
              return <CheckoutMessage key={`${type}-${index}`} componentId={componentId} />;
            case ServiceTypeApi.BookingRedeemer:
              return <BookingRedeemerService key={`${type}-${index}`}/>;
            case ServiceTypeApi.ParticipantsBadge:
              return <ParticipantsBadgeForm key={`${type}-${index}`}/>;
            case ServiceTypeApi.SkiAncillaries:
              return <React.Fragment key={`${type}-${index}`}>
                <SkiPassService key={`SkiPass-${index}`}/>
                <SkiMaterialService key={`SkiMaterial-${index}`}/>
                <SkiLessonService key={`SkiLesson-${index}`}/>
              </React.Fragment>;
            case ServiceTypeApi.Luggage:
              return <LuggageService key={`${type}-${index}`} {...item.serviceConfigurations}/>;
            case ServiceTypeApi.CarRental:
              return <React.Fragment key={`${type}-${index}`}>
                {!showCarRentalFilteringService && <CarRentalService key={`${type}-${index}`}/>}
                {showCarRentalFilteringService && <CarRentalFilteringService key={`${type}-${index}`}/>}
              </React.Fragment>
            case ServiceTypeApi.CancellationInsurance:
              return <CancellationInsuranceService key={`${type}-${index}`}/>;
            case ServiceTypeApi.TShirt:
              return <TShirtService key={`${type}-${index}`}/>;
            case ServiceTypeApi.Sweater:
              return <SweaterService key={`${type}-${index}`}/>;
            case ServiceTypeApi.Golf:
              return <GolfService key={`${type}-${index}`}/>;
            case ServiceTypeApi.GenericPerUnitComponent:
              if (aName === 'MEAL-VOUCHERS') {
                return <DinnerVouchersService key={`${type}-${index}`}/>;
              }
              return null;
            case ServiceTypeApi.GenericPerParticipantSelection:
              if (aName === 'BRIDGE') {
                return <BridgeService key={`${type}-${index}`}/>;
              }
              return null;
            case ServiceTypeApi.Generic:
              switch (aName) {
                case 'TRANSFER':
                  return <TransferService key={`${type}-${aName}-${index}`} />;
                case 'CARRIAGESKI*':
                  return <SkiCarriageService key={`${type}-${aName}-${index}`} />;
                case 'SHAREDBUS':
                  return <SkiTransferService key={`${type}-${aName}-${index}`} />;
                case 'COMEDY':
                  return <ComedyService key={`${type}-${aName}-${index}`} />;
                case 'VERZORGING*':
                  return <MealplanService key={`${type}-${aName}-${index}`} />;
                case 'ACCOMMODATION':
                  return <MealplanDPService key={`${type}-${aName}-${index}`} />;
                case 'HALFBOARD*':
                  return <MealplanExternalService key={`${type}-${aName}-${index}`} />;
                case 'BREAKFAST':
                  return <BreakfastService key={`${type}-${aName}-${index}`} />;
                case 'DINNER':
                  return <DinnerService key={`${type}-${aName}-${index}`} />;
                case 'BREAKFARRHINT_00':
                  return <BreakfastArrivalBusService key={`${type}-${aName}-${index}`} />;
                case 'REBOOKING':
                  return <RebookingService key={`${type}-${aName}-${index}`} />;
                case 'TRAVEL*':
                  return <TravelInsuranceService key={`${type}-${aName}-${index}`} />;
                case 'DUTCHWEEK':
                  return <DutchweekService key={`${type}-${aName}-${index}`} />;
                case 'DUTCHWEEK1':
                  return <DutchweekValThorensService key={`${type}-${aName}-${index}`} />;
                case 'PARTY*':
                  return <PartyTicketsService key={`${type}-${aName}-${index}`} />;
                case 'BABY*':
                  return <BabyCotService key={`${type}-${aName}-${index}`} />;
                case 'PARKEREN':
                  return <ParkingAirportService key={`${type}-${aName}-${index}`} />;
                case 'VISA*':
                  return <VisaService key={`${type}-${aName}-${index}`} />;
                case 'ESIM':
                  return <EsimService key={`${type}-${aName}-${index}`} />;
                case 'FIETS':
                  return <BikeRentalService key={`${type}-${aName}-${index}`} />;
                default:
                  if (window.newrelic) {
                    const error = new Error(`[CheckoutSteps] No service ${type} found for aName: ${aName}`);
                    window.newrelic.noticeError(error);
                  }
                  return null;
              }
            default:
              if(window.newrelic){
                const error = new Error(`[CheckoutSteps] No service ${type} found`);
                window.newrelic.noticeError(error);
              }
              return null;
          }
        })
      }
    </Wrapper>
  );
}
